import type {
    CancelDevelopment,
    CreateDevelopment,
    DeleteDevelopment,
    ExportDevelopmentsExcel,
    ImpedeDevelopment,
    ImportDevelopmentsExcel,
    ListProjectJiraEpics,
    LoadDevelopmentById,
    LoadDevelopments,
    ReactivateDevelopment,
    ResumeDevelopment,
    StartDevelopment,
    UpdateDevelopment
} from '@/domain/use-cases/developments.js';
import { BaseControllerImpl } from '@/presentation/controllers/base/implementation.js';
import type { DevelopmentCategory } from '@/shared/utils/format-status.js';

export class DevelopmentController extends BaseControllerImpl {
    constructor(
        private readonly loadDevelopmentsUseCase: LoadDevelopments,
        private readonly loadDevelopmentByIdUseCase: LoadDevelopmentById,
        private readonly createDevelopmentUseCase: CreateDevelopment,
        private readonly updateDevelopmentUseCase: UpdateDevelopment,
        private readonly cancelDevelopmentUseCase: CancelDevelopment,
        private readonly reactivateDevelopmentUseCase: ReactivateDevelopment,
        private readonly impedeDevelopmentUseCase: ImpedeDevelopment,
        private readonly resumeDevelopmentUseCase: ResumeDevelopment,
        private readonly deleteDevelopmentUseCase: DeleteDevelopment,
        private readonly startDevelopmentUseCase: StartDevelopment,
        private readonly exportDevelopmentsExcelUseCase: ExportDevelopmentsExcel,
        private readonly importDevelopmentsExcelUseCase: ImportDevelopmentsExcel,
        private readonly listProjectJiraEpicsUseCase: ListProjectJiraEpics
    ) {
        super();
    }

    public async loadDevelopments(params?: { search?: string; statuses?: string[]; projectId?: string; page?: number; limit?: number; includeCanceled?: boolean }) {
        return this.loadDevelopmentsUseCase.execute(params);
    }

    public async exportDevelopments(params?: { search?: string; statuses?: string[]; projectId?: string; includeCanceled?: boolean }) {
        return this.exportDevelopmentsExcelUseCase.execute(params);
    }

    public async listProjectJiraEpics(projectId: string) {
        return this.listProjectJiraEpicsUseCase.execute(projectId);
    }

    public async loadDevelopmentById(id: string) {
        return this.loadDevelopmentByIdUseCase.execute(id);
    }

    public async createDevelopment(params: {
        title: string;
        description: string;
        businessProcessId?: string;
        projectId: string;
        consultorId?: string;
        usuarioChaveId?: string;
        linkedDevelopmentId?: string;
        functionalComplexity: 'MUITO_BAIXA' | 'BAIXA' | 'MEDIA' | 'ALTA' | 'MUITO_ALTA' | null;
        functionalHours: number | null;
        techComplexity: 'MUITO_BAIXA' | 'BAIXA' | 'MEDIA' | 'ALTA' | 'MUITO_ALTA' | null;
        techHours: number | null;
        estimatedDeliveryDate: string | null;
        category: DevelopmentCategory | null;
        jiraEpicKey?: string | null;
    }) {
        return this.createDevelopmentUseCase.execute(params);
    }

    public async updateDevelopment(
        id: string,
        params: {
            title?: string;
            description?: string;
            businessProcessId?: string | null;
            consultorId?: string;
            usuarioChaveId?: string;
            linkedDevelopmentId?: string | null;
            functionalComplexity?: 'MUITO_BAIXA' | 'BAIXA' | 'MEDIA' | 'ALTA' | 'MUITO_ALTA' | null;
            functionalHours?: number | null;
            category?: DevelopmentCategory | null;
        }
    ) {
        return this.updateDevelopmentUseCase.execute(id, params);
    }

    public async cancelDevelopment(id: string, reasonId: string, justification: string) {
        return this.cancelDevelopmentUseCase.execute(id, { reasonId, justification });
    }

    public async reactivateDevelopment(id: string) {
        return this.reactivateDevelopmentUseCase.execute(id);
    }

    public async impedeDevelopment(id: string, reasonId: string, description: string) {
        return this.impedeDevelopmentUseCase.execute(id, { reasonId, description });
    }

    public async resumeDevelopment(id: string) {
        return this.resumeDevelopmentUseCase.execute(id);
    }

    public async deleteDevelopment(id: string) {
        return this.deleteDevelopmentUseCase.execute(id);
    }

    public async startDevelopment(id: string, params: { startDate: string; developers?: Array<{ userId: string; isLead: boolean }> }) {
        return this.startDevelopmentUseCase.execute(id, params);
    }

    public async importDevelopments(file: File, projectId: string) {
        return this.importDevelopmentsExcelUseCase.execute(file, projectId);
    }
}
